import React                from 'react';
import PropTypes            from 'prop-types';
import Flux                 from '../../flux/Flux';
import GlobalSearchDropdown from "./GlobalSearchDropdown";
import ReloadHelper         from "../../utils/ReloadHelper";
import Translations         from "../../utils/Translations";
import { generateKey } from '../../utils/CommonUtils';

const MIN_SEARCH_PATTERN_LENGTH = 3;
const LOGO_BREAKPOINT_WIDTH     = 1400;

class GlobalSearch extends React.PureComponent {

	static whichAnimationEvent() {
		let t;
		const el = document.createElement("fakeelement");

		const animations = {
			"animation":       "animationend",
			"OAnimation":      "oAnimationEnd",
			"MozAnimation":    "animationend",
			"WebkitAnimation": "webkitAnimationEnd",
		};

		for (t in animations) {
			if (el.style[t] !== undefined) {
				return animations[t];
			}
		}
	}

	static getInitialState() {
		return {
			inputFocused:          false,
			searchPattern:         '', // current pattern
			lastSearchPattern:     '', // last pattern that actually got searched for
			moreLink:              '',
			resultAvailable:       false,
			active:                false,
			smallLogo:             Flux.Browser.getWindowSize().width < LOGO_BREAKPOINT_WIDTH,
			topResult:             null,
			proposals:             null,
			actorsSearchResult:    null,
			videosSearchResult:    null,
			mediathekSearchResult: null,
			blogSearchResult:      null,
			suggestions:           Flux.GlobalSearch.getSuggestions(),
			lastSearchRequests:    Flux.GlobalSearch.getLastSearchRequests(),
			searchUrl:             Flux.GlobalSearch.getSearchUrl(),
		};
	}

	constructor(props) {
		super(props);
		this.state = GlobalSearch.getInitialState();

		this.animationRef       = null;
		this.animationEventType = GlobalSearch.whichAnimationEvent();

		this.setSearchPattern          = this.setSearchPattern.bind(this);
		this.onOverlayVisibilityChange = this.onOverlayVisibilityChange.bind(this);
		this.exit                      = this.exit.bind(this);
		this.onSearchResult            = this.onSearchResult.bind(this);
		this.onExitClick               = this.onExitClick.bind(this);
		this.onWindowResize            = this.onWindowResize.bind(this);
		this.onSearchResultClick       = this.onSearchResultClick.bind(this);
		this.onOverlayClick            = this.onOverlayClick.bind(this);
		this.onKeyPress                = this.onKeyPress.bind(this);
		this.onInputChange             = this.onInputChange.bind(this);
		this.onLastSearchClick         = this.onLastSearchClick.bind(this);
		this.clearSearchPattern        = this.clearSearchPattern.bind(this);
		this.onLogoClick               = this.onLogoClick.bind(this);
		this.navigateToAllResults      = this.navigateToAllResults.bind(this);
		this.onProposalClick           = this.onProposalClick.bind(this);

		// Sub-Components
		this.getDropdown   = this.getDropdown.bind(this);
		this.getLogo       = this.getLogo.bind(this);
		this.getSearchBar  = this.getSearchBar.bind(this);
		this.getExitButton = this.getExitButton.bind(this);
	}

	componentDidMount() {
		Flux.GlobalSearch.addOverlayVisibilityChangeListener(this.onOverlayVisibilityChange);
		Flux.GlobalSearch.addSearchResultListener(this.onSearchResult);
		Flux.Browser.addWindowResizeListener(this.onWindowResize);
	}

	componentDidUpdate() {
		// Apply post-animation changes (Thank you IE)
		if (this.animationRef) {
			const classNames = ['.global_search-top-bar', '.global_search-search-container', '.global_search-input'];

			const f = (e, classNames) => {
				for (let i = 0; i < classNames.length; i++) {
					const el = document.querySelector(classNames[i]);
					if (el) {
						el.classList.add('-post-animate');
					}
				}
				this.animationRef.removeEventListener(this.animationEventType, e => (f(e, classNames)));
			};

			this.animationRef.removeEventListener(this.animationEventType, e => (f(e, classNames)));
			this.animationRef.addEventListener(this.animationEventType, e => (f(e, classNames)));
		}
	}

	componentWillUnmount() {
		Flux.GlobalSearch.removeOverlayVisibilityChangeListener(this.onOverlayVisibilityChange);
		Flux.GlobalSearch.removeSearchResultListener(this.onSearchResult);
		Flux.Browser.removeWindowResizeListener(this.onWindowResize);
	}

	onWindowResize() {
		const size = Flux.Browser.getWindowSize();

		if (size.width < LOGO_BREAKPOINT_WIDTH) {
			this.setState({smallLogo: true});
		} else if (this.state.smallLogo) {
			this.setState({smallLogo: false});
		}
	}

	onSearchResult() {
		const searchResult = Flux.GlobalSearch.getSearchResult();

		if (searchResult) {
			if (searchResult.searchPattern !== this.state.searchPattern) {
				// old result
				return;
			}
			this.setState({
				topResult:             null, // not yet implemented
				proposals:             searchResult.proposals,
				actorsSearchResult:    searchResult.actors,
				videosSearchResult:    searchResult.videos,
				mediathekSearchResult: searchResult.mediathek,
				blogSearchResult:      searchResult.blog,
				resultAvailable:       true,
				lastSearchRequests:    Flux.GlobalSearch.getLastSearchRequests(),
				lastSearchPattern:     searchResult.searchPattern,
			});
		} else {
			this.setState({
				resultAvailable: true,
			});
		}
	}

	onOverlayVisibilityChange(visible) {
		this.setState({active: visible});
	}

	onExitClick() {
		this.exit();
	}

	navigateToAllResults(e) {
		let url = '';
		if (this.state.actorsSearchResult && this.state.actorsSearchResult.count > 0) {
			url = this.state.actorsSearchResult.link;
		} else if (this.state.videosSearchResult && this.state.videosSearchResult.count > 0) {
			url = this.state.videosSearchResult.link;
		} else if (this.state.mediathekSearchResult && this.state.mediathekSearchResult.count > 0) {
			url = this.state.mediathekSearchResult.link;
		} else if (this.state.blogSearchResult && this.state.blogSearchResult.count > 0) {
			url = this.state.blogSearchResult.link;
		}

		if (url) {
			ReloadHelper.reload(url, e);
			this.onSearchResultClick();
		}
	}

	onSearchResultClick(store = true) {
		if (store) {
			Flux.GlobalSearch.storeSearchRequest(this.state.searchPattern);
		}
		this.exit();
	}

	exit() {
		const state = GlobalSearch.getInitialState();
		this.setState(state, () => {
			document.querySelector('#navbar-search').classList.remove('-hidden');
		});
	}

	onLastSearchClick(searchPattern, e) {
		e.preventDefault();
		e.stopPropagation();
		ReloadHelper.reload(this.state.searchUrl.replace('%s', searchPattern), e);
		this.exit();
	}

	setSearchPattern(searchPattern, skipTimeout = false) {
		const keyPattern = /^[a-z0-9äöüÄÖÜßÁáÉéÍíÓóÚúÑñ¡¿!? -]*$/i; // @see ArgumentFilter

		if (!keyPattern.test(searchPattern)) {
			return;
		} else if (searchPattern.length < MIN_SEARCH_PATTERN_LENGTH) {
			this.setState({
				resultAvailable: false,
				searchPattern:   searchPattern,
			});
		} else {
			this.setState({searchPattern: searchPattern});
			if (skipTimeout) {
				Flux.GlobalSearch.doSearch(searchPattern);
			} else {
				setTimeout(() => {
					if (searchPattern === this.state.searchPattern) {
						Flux.GlobalSearch.doSearch(searchPattern);
					}
				}, 200);
			}
		}

	}

	clearSearchPattern(e) {
		e.preventDefault();
		e.stopPropagation();
		this.setSearchPattern('');
	}

	onInputChange(e) {
		const searchPattern = e.target.value;

		this.setSearchPattern(searchPattern);
	}

	onOverlayClick(e) {
		e.preventDefault();
		e.stopPropagation();
		this.exit();
	}

	defaultOnClick(e) {
		e.preventDefault();
		e.stopPropagation();
	}

	onKeyPress(e) {
		switch (e.key) {
			case 'Escape':
				this.exit();
				break;
			default:
				break;
		}
	}

	onLogoClick(e) {
		ReloadHelper.reload('/', e);
		this.exit();
	}

	onProposalClick(proposal, e) {
		e.preventDefault();
		e.stopPropagation();

		this.setSearchPattern(proposal, true);
	}

	getDropdown() {
		return <GlobalSearchDropdown
			topResult={this.state.topResult}
			proposals={this.state.proposals}
			actorsSearchResult={this.state.actorsSearchResult}
			videosSearchResult={this.state.videosSearchResult}
			mediathekSearchResult={this.state.mediathekSearchResult}
			blogSearchResult={this.state.blogSearchResult}
			postSearch={this.state.resultAvailable}
			suggestions={this.state.suggestions}
			lastSearchRequests={this.state.lastSearchRequests}
			onElementClick={this.onSearchResultClick}
			onLastSearchClick={this.onLastSearchClick}
			searchPattern={this.state.lastSearchPattern}
			onAllResultsClick={(e) => {
				this.navigateToAllResults(e);
			}}
			onProposalClick={this.onProposalClick}
		       />;
	}

	getLogo(idx) {
		const src = Flux.Browser.isSexole() ?  '/assets/img/sexole/sexole_x_visit-x_logo.svg' : (this.state.smallLogo ? '/assets/img/vxlogo-single-icon.svg' : '/assets/img/logo2.svg');

		return <img onClick={this.onLogoClick}
		            key={generateKey('logo', idx)}
					alt={"Visit x logo"}
		            className={"global_search-logo" + (this.state.smallLogo ? ' -small' : '')}
		            src={src}
		       />;
	}

	getSearchBar(idx) {
		const input = (
			<span className="global_search-input-container">
                <form onSubmit={this.navigateToAllResults}>
                    <input onInput={this.onInputChange}
                        value={this.state.searchPattern}
                        placeholder={Translations.get('NavbarSearchSearchFor')}
                        className="global_search-input"
                        autoFocus={true}
                        type="text"
                    />
                </form>
			</span>
		);

		const icon = (
			<span className="global_search-icon">
				<img src={"/assets/icons/lupe.png"} />
			</span>
		);

		let clearIcon = null;
		if (this.state.searchPattern.length > 0) {
			clearIcon = (
				<button onClick={this.clearSearchPattern} className={"global_search-clear-icon"}>
					<span className={"icon -icon-close-line"} />
				</button>
			);
		}

		const dropdown = this.getDropdown();

		return (
			<span key={generateKey('search-bar', idx)} ref={ref => (this.animationRef = ref)} className={"global_search-animation-wrapper"}>
				<span className={"global_search-search-container"}>
					{icon}{input}{clearIcon}{dropdown}
				</span>
			</span>
		);
	}

	getExitButton(idx) {
		return (
			<span key={generateKey('exit-button', idx)} onClick={this.onExitClick} className={"global_search-search-exit"}>
				<span className={"icon -icon-close-line"} />
			</span>
		);
	}

	render() {
		const content = [];

		if (this.state.active) {
			const topBarContent = [];
			topBarContent.push(this.getLogo(topBarContent.length));
			topBarContent.push(this.getSearchBar(topBarContent.length));
			topBarContent.push(this.getExitButton(topBarContent.length));

			const topBar = <div key={generateKey('top-bar', content.length)} onClick={this.defaultOnClick} className="global_search-top-bar">{topBarContent}</div>;
			content.push(topBar);
		}

		return content.length > 0 ?
			<div onKeyDown={this.onKeyPress}
			     onClick={this.onOverlayClick}
			     className="global_search-overlay"
			     tabIndex={0}
			>{content}</div> : null;
	}
}

GlobalSearch.propTypes = {
	mobile: PropTypes.bool,
};

GlobalSearch.defaultProps = {
	mobile: false,
};

export default GlobalSearch;
